import { useState } from 'react';
import { Box, Button, Popover, Typography, Stack, Chip } from '@mui/material';
import { Update as UpdateIcon } from '@mui/icons-material';
import { useAppVersion } from '@diggens/shared-hooks';

export function UpdateNotificationButton() {
  const { updateAvailable, deployedAt, refresh, dismiss } = useAppVersion('web');
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  if (!updateAvailable) return null;

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleRefresh = () => {
    refresh();
  };

  const handleDismiss = () => {
    dismiss();
    handleClose();
  };

  const open = Boolean(anchorEl);

  return (
    <Box sx={{ width: '100%', px: 2, pb: 1 }}>
      <Button
        fullWidth
        variant="outlined"
        color="primary"
        onClick={handleClick}
        startIcon={<UpdateIcon />}
        sx={{
          justifyContent: 'flex-start',
          textTransform: 'none',
          borderColor: 'primary.main',
          animation: 'pulse 2s infinite',
          '@keyframes pulse': {
            '0%': {
              boxShadow: '0 0 0 0 rgba(25, 118, 210, 0.4)',
            },
            '50%': {
              boxShadow: '0 0 0 4px rgba(25, 118, 210, 0)',
            },
            '100%': {
              boxShadow: '0 0 0 0 rgba(25, 118, 210, 0)',
            },
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
          <Typography variant="body2">Update Available</Typography>
          <Chip label="New" size="small" color="error" sx={{ height: 20, fontSize: '0.65rem' }} />
        </Box>
      </Button>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
      >
        <Box sx={{ p: 2, maxWidth: 300 }}>
          <Stack spacing={2}>
            <Typography variant="subtitle1" fontWeight="bold">
              Update Available
            </Typography>
            <Typography variant="body2" color="text.secondary">
              A new version of the app has been deployed.
              {deployedAt && (
                <>
                  <br />
                  <Typography variant="caption" color="text.disabled">
                    Deployed: {new Date(deployedAt).toLocaleString()}
                  </Typography>
                </>
              )}
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button variant="contained" color="primary" onClick={handleRefresh} fullWidth>
                Refresh Now
              </Button>
              <Button variant="outlined" onClick={handleDismiss} fullWidth>
                Dismiss
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Popover>
    </Box>
  );
}
